import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import AuthShell from "@/components/AuthShell";
import api, { formatApiErrorDetail } from "@/lib/api";

const inputCls =
  "w-full rounded-xl border border-white/12 bg-white/5 px-4 py-3 text-white placeholder:text-slate-500 focus:border-arjuna-teal focus:outline-none focus:ring-1 focus:ring-arjuna-teal/40 transition-colors duration-300";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      toast.success("Password updated. Please sign in.");
      navigate("/login");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Set new password"
      subtitle="Choose a strong password for your account."
      footer={
        <Link to="/login" className="text-arjuna-teal hover:underline">
          Back to sign in
        </Link>
      }
    >
      {!token ? (
        <div className="rounded-xl border border-white/10 bg-white/5 p-6 text-slate-300" data-testid="reset-invalid">
          This reset link is invalid or missing a token.
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
          <div>
            <label className="overline mb-2 block">New password</label>
            <input data-testid="reset-password" type="password" required className={inputCls} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" />
          </div>
          <button type="submit" disabled={loading} data-testid="reset-submit" className="btn-primary w-full">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {loading ? "Updating…" : "Update password"}
          </button>
        </form>
      )}
    </AuthShell>
  );
}
